"use client";

import { getMonthName, formatEventType } from "@/lib/events";

interface EventFiltersProps {
  months: number[];
  countries: string[];
  boatClasses: string[];
  eventTypes: string[];
  selectedMonth: number | null;
  selectedCountry: string | null;
  selectedBoatClasses: string[];
  selectedEventType: string | null;
  onMonthChange: (month: number | null) => void;
  onCountryChange: (country: string | null) => void;
  onBoatClassToggle: (boatClass: string) => void;
  onEventTypeChange: (eventType: string | null) => void;
  onClear: () => void;
}

export function EventFilters({
  months,
  countries,
  boatClasses,
  eventTypes,
  selectedMonth,
  selectedCountry,
  selectedBoatClasses,
  selectedEventType,
  onMonthChange,
  onCountryChange,
  onBoatClassToggle,
  onEventTypeChange,
  onClear,
}: EventFiltersProps) {
  const hasFilters =
    selectedMonth !== null ||
    selectedCountry !== null ||
    selectedBoatClasses.length > 0 ||
    selectedEventType !== null;

  return (
    <div className="space-y-4 rounded-lg border border-gray-200 p-4 dark:border-gray-700">
      <div className="flex flex-wrap gap-4">
        {/* Month filter */}
        <div>
          <label className="mb-1 block text-xs font-medium text-gray-500 dark:text-gray-400">
            Month
          </label>
          <select
            value={selectedMonth ?? ""}
            onChange={(e) =>
              onMonthChange(e.target.value ? Number(e.target.value) : null)
            }
            className="rounded-md border border-gray-300 bg-white px-3 py-1.5 text-sm dark:border-gray-600 dark:bg-gray-800"
          >
            <option value="">All months</option>
            {months.map((m) => (
              <option key={m} value={m}>
                {getMonthName(m)}
              </option>
            ))}
          </select>
        </div>

        {/* Country filter */}
        <div>
          <label className="mb-1 block text-xs font-medium text-gray-500 dark:text-gray-400">
            Country
          </label>
          <select
            value={selectedCountry ?? ""}
            onChange={(e) =>
              onCountryChange(e.target.value || null)
            }
            className="rounded-md border border-gray-300 bg-white px-3 py-1.5 text-sm dark:border-gray-600 dark:bg-gray-800"
          >
            <option value="">All countries</option>
            {countries.map((c) => (
              <option key={c} value={c}>
                {c}
              </option>
            ))}
          </select>
        </div>

        {/* Event type filter */}
        <div>
          <label className="mb-1 block text-xs font-medium text-gray-500 dark:text-gray-400">
            Event Type
          </label>
          <select
            value={selectedEventType ?? ""}
            onChange={(e) =>
              onEventTypeChange(e.target.value || null)
            }
            className="rounded-md border border-gray-300 bg-white px-3 py-1.5 text-sm dark:border-gray-600 dark:bg-gray-800"
          >
            <option value="">All types</option>
            {eventTypes.map((t) => (
              <option key={t} value={t}>
                {formatEventType(t)}
              </option>
            ))}
          </select>
        </div>

        {hasFilters && (
          <div className="flex items-end">
            <button
              onClick={onClear}
              className="rounded-md px-3 py-1.5 text-sm text-gray-500 hover:text-primary dark:text-gray-400"
            >
              Clear filters
            </button>
          </div>
        )}
      </div>

      {/* Boat class multi-select */}
      <div>
        <label className="mb-2 block text-xs font-medium text-gray-500 dark:text-gray-400">
          Boat Classes
        </label>
        <div className="flex flex-wrap gap-2">
          {boatClasses.map((bc) => {
            const isSelected = selectedBoatClasses.includes(bc);
            return (
              <button
                key={bc}
                onClick={() => onBoatClassToggle(bc)}
                className={`rounded-full border px-3 py-1 text-sm transition-colors ${
                  isSelected
                    ? "border-primary bg-primary text-white"
                    : "border-gray-300 text-gray-600 hover:border-primary hover:text-primary dark:border-gray-600 dark:text-gray-300"
                }`}
              >
                {bc}
              </button>
            );
          })}
        </div>
      </div>
    </div>
  );
}
